import { injectable } from 'tsyringe';
import { authenticatedFetch } from '@/shared/auth/AuthenticatedFetch';

const BASE = '/_billing/api/billing';

export interface BillingInvoice {
    id: string;
    number: string;
    status: string;
    totalMinor: number;
    currency: string;
    periodStart: string | null;
    periodEnd: string | null;
    issuedAt: string | null;
    paidAt: string | null;
}

export interface BillingOverview {
    provider: 'stripe' | 'none';
    publishableKey: string | null;
    card: { brand: string | null; last4: string | null; expMonth: number | null; expYear: number | null } | null;
    subscription: { planName: string; priceMinor: number; currency: string; status: string; currentPeriodEnd: string | null } | null;
    invoices: BillingInvoice[];
}

/** Tenant-facing billing: overview + card-on-file (Stripe Elements) via the BFF. */
@injectable()
export class BillingService {
    async getOverview(): Promise<BillingOverview> {
        const res = await authenticatedFetch(`${BASE}/overview`);
        if (!res.ok) throw new Error(`Failed to load billing (${res.status})`);
        return (await res.json()) as BillingOverview;
    }

    /** Begin saving a card — returns the SetupIntent client secret for Stripe.js. */
    async createSetupIntent(): Promise<{ clientSecret: string; customerId: string }> {
        const res = await authenticatedFetch(`${BASE}/card/setup-intent`, { method: 'POST' });
        if (!res.ok) throw new Error(`Failed to start card setup (${res.status})`);
        return (await res.json()) as { clientSecret: string; customerId: string };
    }

    /** Resolve an invoice's Stripe PDF URL and open it in a new tab. */
    async openInvoicePdf(invoiceId: string): Promise<void> {
        const res = await authenticatedFetch(`${BASE}/invoices/${invoiceId}/pdf`);
        if (!res.ok) throw new Error(`Failed to open invoice (${res.status})`);
        const { url } = (await res.json()) as { url: string };
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    /** Record the confirmed payment method as the default card. */
    async confirmCard(paymentMethodId: string): Promise<void> {
        const res = await authenticatedFetch(`${BASE}/card/confirm`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ paymentMethodId }),
        });
        if (!res.ok) throw new Error(`Failed to save card (${res.status})`);
    }
}
